import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle, RefreshCw, Home } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { isChunkLoadError, reloadForNewBuild } from '@/lib/chunkReload'

interface Props {
  children: ReactNode
  pageName?: string
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

/**
 * Page-level error boundary — wraps individual pages so a crash in one
 * doesn't take down the entire app. Shows a retry button and a link home.
 */
export class PageErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, errorInfo: null }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo })
    console.error(`[PageErrorBoundary] ${this.props.pageName || 'Page'} crashed:`, error, errorInfo)
    // A tab that outlived a UI release asks for a chunk the new build renamed: reload once.
    if (isChunkLoadError(error)) reloadForNewBuild()

    // Report to Sentry if available
    type SentryGlobal = {
      Sentry?: {
        captureException: (e: unknown, ctx?: { extra?: Record<string, unknown> }) => void
      }
    }
    const w = typeof window !== 'undefined' ? (window as unknown as SentryGlobal) : undefined
    if (w?.Sentry) {
      w.Sentry.captureException(error, { extra: { errorInfo, pageName: this.props.pageName } })
    }
  }

  handleRetry = () => {
    // Re-rendering would request the same missing chunk; only a reload loads the new build.
    if (isChunkLoadError(this.state.error)) {
      window.location.reload()
      return
    }
    this.setState({ hasError: false, error: null, errorInfo: null })
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[50vh] px-4 text-center">
          <div className="rounded-full bg-red-500/10 p-4 mb-6">
            <AlertTriangle className="h-10 w-10 text-red-400" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">
            {this.props.pageName ? `${this.props.pageName} encountered an error` : 'Something went wrong'}
          </h2>
          <p className="text-sm text-gray-400 max-w-md mb-2">
            An unexpected error occurred. You can try refreshing this page or go back to the dashboard.
          </p>
          {this.state.error && (
            <details className="text-xs text-gray-400 max-w-lg mb-6 cursor-pointer">
              <summary className="hover:text-gray-300">Error details</summary>
              <pre className="mt-2 p-3 bg-gray-800 rounded text-left overflow-auto max-h-32">
                {this.state.error.message}
              </pre>
            </details>
          )}
          <div className="flex gap-3">
            <Button variant="outline" onClick={this.handleRetry}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Try again
            </Button>
            <Button variant="ghost" onClick={() => (window.location.href = '/dashboard')}>
              <Home className="h-4 w-4 mr-2" />
              Go to Dashboard
            </Button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
